import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Loader2 } from "lucide-react";
import { GolfButton } from "@/components/golf/golf-button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { getVideoById, VideoRecord } from "@/lib/videos";
import { edgeInvoke } from "@/lib/edgeInvoke";
import { getThumbnailUrl } from "@/lib/thumbnail";


export default function Video() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [video, setVideo] = useState<VideoRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const refreshTimer = useRef<any>(null);

  const loadVideo = async () => {
    if (!user || !id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await getVideoById(id);

      if (error) throw error;

      setVideo(data);
    } catch (error) {
      console.error('Error loading video:', error);
      setError('Failed to load video');
      toast({
        title: "Error loading video",
        description: "This video could not be found or you don't have access to it",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPlaybackUrl = async (playbackId: string) => {
    try {
      const result = await edgeInvoke('playback-url', { playback_id: playbackId });
      return result.url as string;
    } catch (error) {
      console.error('Failed to get signed playback URL:', error);
      throw error;
    }
  };

  const resolveVideoUrl = async () => {
    if (!video?.mux_playback_id) {
      setVideoSrc('');
      return;
    }

    const pid = video.mux_playback_id;

    try {
      const url = await getPlaybackUrl(pid);
      setVideoSrc(url);
      
      // Clear any existing refresh timer since we're using the new unified approach
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
    } catch (e) {
      console.error('Error resolving video URL:', e);
      // Fallback to public URL
      try {
        setVideoSrc(`https://stream.mux.com/${pid}.m3u8`);
      } catch (fallbackError) {
        console.error('Failed to set fallback URL:', fallbackError);
        setVideoSrc('');
      }
    }
  };

  const loadThumbnail = async () => {
    if (!video?.mux_playback_id) {
      setThumbnailUrl('');
      return;
    }

    try {
      const url = await getThumbnailUrl(video.mux_playback_id, video.playback_policy);
      setThumbnailUrl(url);
    } catch (error) {
      console.error('Error loading thumbnail:', error);
      // SECURITY: No public URL fallbacks allowed - all videos must be signed
      setThumbnailUrl(''); // No thumbnail if signing fails
    }
  };

  useEffect(() => {
    loadVideo();
  }, [user?.id, id]);

  useEffect(() => {
    if (video) {
      resolveVideoUrl();
      loadThumbnail();
    }
    return () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
      }
    };
  }, [video?.mux_playback_id, video?.playback_policy]);

  const handleAnalyzeClick = () => {
    navigate('/analyze', { state: { videoId: id } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="flex items-center gap-2">
          <Loader2 size={24} className="animate-spin" />
          <span>Loading video...</span>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Video not found</h2>
          <p className="text-muted-foreground mb-4">
            This video could not be found or you don't have access to it.
          </p>
          <GolfButton onClick={() => navigate('/videos')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Videos
          </GolfButton>
        </div>
      </div>
    );
  }

  if (video.status !== 'ready') {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="px-6 pt-8">
          <div className="flex items-center gap-4 mb-6">
            <GolfButton
              variant="secondary"
              size="sm"
              onClick={() => navigate('/videos')}
            >
              <ArrowLeft size={16} className="mr-2" />
              Back
            </GolfButton>
            <h1 className="text-2xl font-bold">
              Video {video.id.slice(0, 8)}...
            </h1>
          </div>

          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              Video is still {video.status}...
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Your video will be available for viewing once processing is complete.
            </p>
            <GolfButton onClick={() => navigate('/videos')}>
              <ArrowLeft size={16} className="mr-2" />
              Back to Videos
            </GolfButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="px-6 pt-8">
        <div className="flex items-center gap-4 mb-6">
          <GolfButton
            variant="secondary"
            size="sm"
            onClick={() => navigate('/videos')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </GolfButton>
          <h1 className="text-2xl font-bold">
            Video {video.id.slice(0, 8)}...
          </h1>
        </div>

        <div className="space-y-6">
          {videoSrc ? (
            <div className="w-full max-w-2xl mx-auto">
              <video 
                controls 
                playsInline 
                webkit-playsinline="true"
                crossOrigin="anonymous"
                preload="metadata"
                className="w-full rounded-lg"
                src={videoSrc}
                poster={thumbnailUrl}
              />
            </div>
          ) : video.mux_playback_id ? (
            <div className="w-full max-w-2xl mx-auto aspect-video bg-muted/20 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Loader2 size={24} className="animate-spin mx-auto mb-2" />
                <p className="text-muted-foreground">Loading video...</p>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-2xl mx-auto aspect-video bg-muted/20 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Play size={48} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">Video not available for playback</p>
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-1">
            <p>Uploaded: {new Date(video.created_at).toLocaleDateString()}</p>
            {video.duration && (
              <p>Duration: {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</p>
            )}
            {video.width && video.height && (
              <p>Resolution: {video.width}×{video.height}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}